import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableModule, TableLazyLoadEvent } from 'primeng/table';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CustomersService } from '../customers.service';
import { Customer } from '../customers.model';
import { Page } from '../../../models/page-wrapper.model';
import * as XLSX from 'xlsx';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
@Component({
  selector: 'app-customers-list',
  templateUrl: './customers-list.component.html',
  styleUrl: './customers-list.component.css',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    ReactiveFormsModule,
  ],
  providers: [ConfirmationService, MessageService],
})
export class CustomersListComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  customers: Customer[] = [];
  totalRecords = 0;
  isLoading = false;
  filterForm!: FormGroup;
  private filterSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private customersService: CustomersService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.buildFilterForm();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500), // Wait for 500ms of inactivity
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(() => {
        // Reset paginator to the first page and trigger a reload
        this.dt.reset();
      });
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      customerName: [''],
      customerNumber: [''],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      customerName: '',
      customerNumber: '',
    });
    this.dt.reset();
  }

  loadCustomers(
    event?: TableLazyLoadEvent,
    isExport: boolean = false,
    callback?: (customers: any[]) => void
  ) {
    this.isLoading = true;
    let params: any;

    if (isExport) {
      params = {
        page: 0,
        size: 2000,
        customerName: this.filterForm.value.customerName,
        customerNumber: this.filterForm.value.customerNumber,
      };
    } else {
      const size = event?.rows ?? 10;
      const page = event?.first ? Math.floor(event.first / size) : '0';
      params = {
        page,
        size,
        customerName: this.filterForm.value.customerName,
        customerNumber: this.filterForm.value.customerNumber,
      };
    }

      Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.customersService.getAllCustomers(params).subscribe({
      next: (page: Page<Customer>) => {
        this.customers = page.content || [];
        this.totalRecords = page.totalElements;
        this.isLoading = false;
        if (callback) {
          callback(page.content);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Customers units.',
        });
      },
    });
  }

  deleteCustomer(customer: Customer): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${customer.customerName}?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.customersService.deleteCustomer(customer.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Customer deleted successfully',
            });
            this.loadCustomers();
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete Customer.',
            });
          },
        });
      },
    });
  }

  exportExcel(): void {
    this.loadCustomers(undefined, true, (customers: any[]) => {
      const dataToExport = customers.map((customer) => ({
        'Customer Name': customer.customerName,
        'Customer Number': customer.customerNumber,
        'ERP Customer ID': customer.erpCustomerId,
        'Org ID': customer.orgId ?? '-',
        Status: customer.status ?? '-',
        'Calculation Method': customer.calculationMethod ?? '-',
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'business-units');

      XLSX.writeFile(wb, 'Business_Unit_Export.xlsx');
    });
  }
}
